/**
 * useEmployeeDocumentMasterListScreen.js
 * --------------------------------------
 * Hook for Employee Document Master list screen using shared useMasterList
 * and asynchronous JobEngine export.
 */
import { useState, useEffect } from 'react';
import { useMasterList } from '../../common/useMasterList';
import * as employeeDocumentMasterApi from '../api/employeeDocumentMasterApi';
import { useExportJob } from '../../../../hooks/useExportJob';
import { executePrintReport } from '../../../../utils/printReportUtil';

export function useEmployeeDocumentMasterListScreen() {
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    async function loadCategories() {
      try {
        const data = await employeeDocumentMasterApi.getDocumentCategories();
        setCategories(data || []);
      } catch (err) {
        console.error('Failed to load document categories:', err);
      }
    }
    loadCategories();
  }, []);

  const master = useMasterList({
    entityName: 'Employee Document Template',
    apiClient: employeeDocumentMasterApi.employeeDocumentMasterClient,
    fetchList: employeeDocumentMasterApi.listDocumentTemplates,
    archiveItem: employeeDocumentMasterApi.archiveDocumentTemplate,
    deleteItem: employeeDocumentMasterApi.deleteDocumentTemplate,
    extraParams: selectedCategory ? { cat_id: Number(selectedCategory) } : {},
  });

  const { triggerExport, isExporting } = useExportJob({
    module: 'employee_document_master',
    fileFormat: 'XLSX',
  });

  const handleToolSelect = (toolKey) => {
    if (toolKey === 'download' || toolKey === 'export_excel') {
      triggerExport({
        viewMode: master.state.viewMode,
        searchText: master.state.searchText,
        cat_id: selectedCategory ? Number(selectedCategory) : null,
      });
    } else if (toolKey === 'print') {
      executePrintReport({
        title: 'Employee Document Master Registry',
        subtitle: 'Official Employee Letter & Document Templates',
        columns: [
          { header: 'ID', accessor: 'id' },
          { header: 'Template Name', accessor: 'name' },
          { header: 'Category', accessor: 'category_name' },
          { header: 'Status', accessor: 'status' },
          { header: 'Created Date', accessor: 'created_on' },
        ],
        rows: master.state.rows || [],
      });
    }
  };

  return {
    ...master,
    state: {
      ...master.state,
      selectedCategory,
      categories,
      isExporting,
    },
    actions: {
      ...master.actions,
      setSelectedCategory,
      handleToolSelect,
    },
  };
}
